import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import "./SelectRecipientsEdit.scss";
import { Close, CloudUpload, Grid, Search } from '@livingdesign/icons';
import EditFormActionsFooter from './EditFormActionsFooter';
import CSVUploadOverlay from './RecipientSelection/CSVUpload/CSVUploadOverlay';
import { useMessageDetailsForm } from '../../../../../contexts/MessageDetailsForm';
import { getCsvFilename, getSelectedToFieldHeaderForCSVFile, getSSOTMapping } from '../../../../../utils/campaignEdit';
import EmailInputField from '../../../common/EmailInputField';
import { Controller } from 'react-hook-form';
import { CAMPAIGN, DATA_SOURCE, DATA_SOURCE_TYPES, RECIPIENTS } from '../../../../../consts/DBFields';
import { useCampaignContext } from '../../../../../contexts/Campaign';
import { CAMPAIGN_TYPES } from '../../../../../consts/Campaign';
import { isStandardCampaign } from '../../../../../utils/campaignEdit';
import { useAppContext } from '../../../../../contexts/Core';
import { FEATURE } from '../../../../../consts/Common';
import _ from "lodash";
import Popover from '../../../common/Popover/PopoverMessage';
import { getCampaignType, isSystemNotificationsCampaign } from '../../../../../utils/campaign';
import InputErrorText from '../../../common/Error/InputErrorText';
import SavedRecipientsOverlay from './RecipientSelection/SavedRecipients/SavedRecipientsOverlay';
import RecipientFilterOverlay from './RecipientSelection/RecipientFilter/RecipientFilterOverlay';
import { useRecipientFilter } from '../../../../../contexts/RecipientFilter';
import AppliedFilterCard from './RecipientSelection/RecipientFilter/AppliedFilterCard';
import UploadedCSVFiles from './FormComponents/UploadedCSVFiles';

export const QA_ID_PREFIX = 'SelectRecipientsEdit';

function SelectRecipientsEdit({
  stepIndex,
}) {

  const {
    isToggleEnabled,
  } = useAppContext();

  const {
    notificationRecipientsFields,
    downloadCampaignCSV,
  } = useCampaignContext();

  const {
    formValues,
    errors,
    control,
    deleteCsvFileV1,
    uploadPageLoading,
    alertModel,
    setAlertModel,
    continueButtonLoading,
    selectedCampaign,
    saveDraft,
    handleContinue,
    handlePrevious,
    showCSVUploadOverlay,
    setShowCSVUploadOverlay,
    notificationIndex,
    isOverlayOn,
    resetManualRecipients,
    getRecipientFieldMappingValue,
    channel,
    handleAutoSave,
    showSavedRecipientsOverlay,
    setShowSavedRecipientsOverlay,
    showRecipientFilterOverlay,
    setShowRecipientFilterOverlay,
    handleSSOTFilterUpdate,
    handleDeleteSSOTFilter,
  } = useMessageDetailsForm();

  const {
    setRawFilter,
    setFilterId,
    parsedFilter,
    validate,
    parseFilter,
  } = useRecipientFilter();

  const isFirstRender = useRef(true);

  const [loading, setLoading] = useState(false);
  const [savedRecipientsEnabled, setSavedRecipientsEnabled] = useState(false);
  const [recipientFiltering, setRecipientFiltering] = useState(false);


  useEffect(() => {
    setSavedRecipientsEnabled(isToggleEnabled(FEATURE.SAVED_RECIPIENTS));
    setRecipientFiltering(isToggleEnabled(FEATURE.RECIPIENT_FILTERING));
  }, []);

  useEffect(() => {
    setLoading(uploadPageLoading || continueButtonLoading);
  }, [uploadPageLoading, continueButtonLoading]);

  useEffect(() => {
    if(isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    console.log(`calling autosae for manual recipients`);
    handleAutoSave();
  }, [formValues.to, formValues.cc, formValues.bcc]);

  const formatCSVHeaderName = (selectedCampaign, notificationIndex, fileName) => {
    const headerName = getSelectedToFieldHeaderForCSVFile(selectedCampaign, notificationIndex, fileName);
    return headerName ? `[${headerName}] ` : "";
  }

  const [typedTextValue, setTypedTextValue] = useState("");
  const [valueFromDropdownSelected, setValueFromDropdownSelected] = useState("");

  const disbaleManualInput = () => {
    if(getCampaignType(selectedCampaign, notificationIndex) === CAMPAIGN_TYPES.personalized.value) return true;
    if(formValues.csvFiles && formValues.csvFiles.length > 0) return true;
    if(isRecipientFilterUsed) return true;
    return false;
  }

  const manualToRecipientFieldValue = useMemo(() => () => {
    const recipientType = RECIPIENTS.TO;
    const value = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, recipientType);
    if(!Array.isArray(value)) return [];
    return value;
  }, [selectedCampaign]);

  const manualCcRecipientFieldValue = useMemo(() => () => {
    const recipientType = RECIPIENTS.CC;
    const value = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, recipientType);
    if(!Array.isArray(value)) return [];
    return value;
  }, [selectedCampaign]);

  const manualBccRecipientFieldValue = useMemo(() => () => {
    const recipientType = RECIPIENTS.BCC;
    const value = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, recipientType);
    if(!Array.isArray(value)) return [];
    return value;
  }, [selectedCampaign]);

  const isManualInputPresent = () => {
    const to = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.TO);
    const cc = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.CC);
    const bcc = getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.BCC);
    
    if(!_.isEmpty(to) || !_.isEmpty(cc) || !_.isEmpty(bcc)) {
      return true;
    }
    return false;
  }

  const isCSVInputPresent = useCallback(() => {
    if(formValues.csvFiles && formValues.csvFiles.length > 0) return true;
    return false;
  }, [formValues.csvFiles]);

  const isRecipientFilterUsed = useMemo(() => {
    if(selectedCampaign[CAMPAIGN.DATA_SOURCE][DATA_SOURCE.TYPE] !== DATA_SOURCE_TYPES.SSOT) return false;
    const ssotConfig = getSSOTMapping(selectedCampaign, notificationIndex);
    if(!ssotConfig) return false;
    return true;
  }, [selectedCampaign]);

  useEffect(() => {
    if(isRecipientFilterUsed) {
      const ssotConfig = getSSOTMapping(selectedCampaign, notificationIndex);
      setRawFilter(ssotConfig?.filter);
      setFilterId(ssotConfig?.id);
    }
  }, [isRecipientFilterUsed]);

  const appliedFilter = useMemo(() => {
    if(!isRecipientFilterUsed) return [];
    const ssotConfig = getSSOTMapping(selectedCampaign, notificationIndex);
    return parseFilter(ssotConfig?.filter || "");
  }, [selectedCampaign]);

  const handleCsvUploadButtonClick = () => {
    if(isStandardCampaign(selectedCampaign, notificationIndex) && isManualInputPresent()) {
      setAlertModel({
        show: true,
        handleClose: () => setAlertModel({ ...alertModel, show: false }),
        title: "Are you sure you want to swap the existing list of recipients with those included in the CSV?",
        message: "This will erase the list of recipients provided manually.",
        confirmButtonText: "Yes",
        cancelButtonText: "Never mind",
        handleConfirm: async () => {
          await resetManualRecipients();
          setShowCSVUploadOverlay(true);
        },
      })
    } else if (isStandardCampaign(selectedCampaign, notificationIndex) && isRecipientFilterUsed) {
      setAlertModel({
        show: true,
        handleClose: () => setAlertModel({ ...alertModel, show: false }),
        title: "Please delete the applied recipient filter before uploading a CSV file.",
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        handleConfirm: async () => {
          setAlertModel({ ...alertModel, show: false });
        },
      })
    } else {
      setShowCSVUploadOverlay(true);
    }
  }

  const handleSavedRecipientsButtonClick = () => {
    setShowSavedRecipientsOverlay(true);
  }

  const handleSavedFilterApplied = async ({ filterName, rawFilter, filterId }) => {
        const parsedRawFilter = parseFilter(rawFilter);
        if(!validate(parsedRawFilter)) {
          setAlertModel({
            show: true,
            handleClose: () => setAlertModel({ ...alertModel, show: false }),
            title: "Invalid Filter",
            message: "Please edit the filter and validate before applying.",
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            handleConfirm: () => setAlertModel({ ...alertModel, show: false }),
          });
        }
        else {
          try {
            setLoading(true);
            setFilterId(filterId);
            setRawFilter(rawFilter);
            await handleSSOTFilterUpdate(rawFilter, filterId);  
          }
          catch(error) {
            console.log('error while saving ssot filter', error)
          }
          finally {
            setShowRecipientFilterOverlay(false);
            setLoading(false);
          }
          
        }
      
  }

  const handleRecipientFilterButtonClick = () => {
    if(isManualInputPresent() || isCSVInputPresent()) {
      setAlertModel({
        show: true,
        handleClose: () => setAlertModel({ ...alertModel, show: false }),
        title: "Cannot apply recipient filters",
        message: "Please remove manually entered recipients or uploaded CSV files before applying recipient filters.",
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        handleConfirm: () => setAlertModel({ ...alertModel, show: false }),
      });
    } else {
      if(isRecipientFilterUsed) {
        const ssotConfig = getSSOTMapping(selectedCampaign, notificationIndex);
        setRawFilter(ssotConfig?.filter);
        setFilterId(ssotConfig?.id);
      }
      setShowRecipientFilterOverlay(true);
    }
  }

  const handleDeleteFilter = async () => {
    setAlertModel({
      show: true,
      handleClose: () => setAlertModel({ ...alertModel, show: false }),
      title: "Are you sure you want to remove the applied recipient filter?",
      confirmButtonText: "Yes",
      cancelButtonText: "Never mind",
      handleConfirm: async () => {
        setLoading(true);
        await handleDeleteSSOTFilter();
        setLoading(false);
        setAlertModel({ ...alertModel, show: false });
      }
    })
  }

  const popoverMessage = useMemo(() => {
    if(getCampaignType(selectedCampaign, notificationIndex) === CAMPAIGN_TYPES.personalized.value) {
      return "Recipients can only be provided via CSV upload when personalized campaigns are selected.";
    }
    if(formValues.csvFiles && formValues.csvFiles.length > 0) {
      return "Please delete the uploaded CSV file to switch to manual recipient inputs.";
    }
    if(isRecipientFilterUsed) {
      return "Please remove the applied recipient filter to switch to manual recipient inputs.";
    }
    return "";
  }, [formValues.csvFiles, formValues.campaignType, isRecipientFilterUsed]);

  const showManualRecipientInputField = useMemo(() => {
    if(channel === 'mail' && !isSystemNotificationsCampaign(selectedCampaign)) return true;
    return false;
  }, [selectedCampaign, channel]);

  return (
    <>
    <div data-testid={`${QA_ID_PREFIX}-container`} style={{display: (isOverlayOn() ? "none" : "block")}}>
      <div data-testid={`${QA_ID_PREFIX}-title`} className='select-recipients-edit-title'>
        <h2 data-testid={`${QA_ID_PREFIX}-title-heading`}>Select recipients for your message</h2>
        <p data-testid={`${QA_ID_PREFIX}-title-description`}>Add recipients using one or more methods: enter them manually, select from saved recipients, apply filters, or upload CSV file.</p>
      </div>
      <div data-testid={`${QA_ID_PREFIX}-form`} className='select-recipients-edit-form'>
        {showManualRecipientInputField && 
        <>
          <div data-testid={`${QA_ID_PREFIX}-required-field-label`} className='typo-1' style={{paddingTop: "24px"}}>*Required field</div>
          <Controller
            name={"to"}
            control={control}
            render={({ field }) => (
              <Popover
                disabled={disbaleManualInput()}
                content={
                  <div
                    data-testid={`${QA_ID_PREFIX}-to-popover-content`}
                    style={{maxWidth: "300px"}}
                  >
                    <p data-testid={`${QA_ID_PREFIX}-to-popover-message`}>{popoverMessage}</p>
                  </div>
                }
              >
                <EmailInputField
                  {...field}
                  componentKey={`add-to-field`}
                  error={errors.to ? errors.to.message : null}
                  disabled={loading || disbaleManualInput()}
                  field={{
                    label: "To*",
                    name: "to",
                    required: false,
                  }}
                  value={manualToRecipientFieldValue || []}
                  handleChange={(value) => {
                    if(!Array.isArray(value)) return;
                    notificationRecipientsFields[RECIPIENTS.TO].handleChange(
                      value
                    );
                  }}
                  getTypedTextValue={setTypedTextValue}
                  setValueFromDropdownSelected={setValueFromDropdownSelected}
                  isLookUp={true}
                  associateOnly={true}
                  includeGroups={true}
                  multiple={true}
                  data-testid={`${QA_ID_PREFIX}-to-email-input`}
                />
              </Popover>
            )}
          />
          <div data-testid={`${QA_ID_PREFIX}-to-field-description`} className='typo-1' style={{paddingTop: "4px"}}>Enter the name or email of your recipients</div>
        </>
        }
        {(errors.to && errors.to.message) &&
          <InputErrorText data-testid={`${QA_ID_PREFIX}-to-error`} message={errors.to.message} />
        }
        {(errors.dataSourceType && errors.dataSourceType.message) &&
          <InputErrorText data-testid={`${QA_ID_PREFIX}-data-source-type-error`} message={errors.dataSourceType.message} />
        }
        <div data-testid={`${QA_ID_PREFIX}-spacer`} style={{marginBottom: "24px"}}></div>
        {!_.isEmpty(formValues.csvFiles) &&
          <>
          {formValues.csvFiles.map((csvFile, index) => {
            return (
              <UploadedCSVFiles
                key={csvFile.name}
                csvFiles={formValues.csvFiles}
                selectedCampaign={selectedCampaign}
                notificationIndex={notificationIndex}
                loading={loading}
                onDeleteFile={async () => {
                  console.log("onDeleteFile");
                  setAlertModel({
                    ...alertModel,
                    title: "Remove CSV File",
                    message: `Are you sure you want to remove the file ${csvFile.actualFilename}?`,
                    handleConfirm: async () => {
                      console.log("handleConfirm");
                      await deleteCsvFileV1();
                      setAlertModel({ ...alertModel, show: false });
                    },
                    show: true,
                    confirmButtonText: "OK",
                    cancelButtonText: "Cancel",
                    handleClose: () => setAlertModel({ ...alertModel, show: false }),
                  });
                }}
                onDownloadFile={async () => {
                  await downloadCampaignCSV(selectedCampaign);
                }}
                alertModel={alertModel}
                setAlertModel={setAlertModel}
              />
            )
          })
        }
        </>
        }
        {isRecipientFilterUsed &&
          <div data-testid={`${QA_ID_PREFIX}-applied-filter-container`} className='typo-1' style={{marginBottom: "24px"}}>
            <AppliedFilterCard
              parsedFilter={appliedFilter}
              handlEditFilter={handleRecipientFilterButtonClick}
              handleDeleteFilter={handleDeleteFilter}
              loading={loading}
            />
          </div>
        }
        <h3 data-testid={`${QA_ID_PREFIX}-recipient-options-heading`}>Please select the available options to add recipients.</h3>
        <div data-testid={`${QA_ID_PREFIX}-recipient-options-container`} className='recipient-options'>
          {(savedRecipientsEnabled && isStandardCampaign(selectedCampaign, notificationIndex)) && 
            <button
              data-testid={`${QA_ID_PREFIX}-saved-recipients-option`}
              className='recipient-option'
              disabled={loading}
              onClick={handleSavedRecipientsButtonClick}
            >
              <div data-testid={`${QA_ID_PREFIX}-saved-recipients-icon`} className='icon-container'><Search size='large' /></div>
              <h4 data-testid={`${QA_ID_PREFIX}-saved-recipients-title`}>Saved filters</h4>
              <div data-testid={`${QA_ID_PREFIX}-saved-recipients-description`}>Select your previously saved recipient filters</div>
            </button>
          }
          {(recipientFiltering && isStandardCampaign(selectedCampaign, notificationIndex)) &&
            <button
              data-testid={`${QA_ID_PREFIX}-recipient-filtering-option`}
              className='recipient-option'
              onClick={handleRecipientFilterButtonClick}
              disabled={loading}
            >
              <div data-testid={`${QA_ID_PREFIX}-recipient-filtering-icon`} className='icon-container'><Grid size='large' /></div>
              <h4 data-testid={`${QA_ID_PREFIX}-recipient-filtering-title`}>Recipient filtering</h4>
              <div data-testid={`${QA_ID_PREFIX}-recipient-filtering-description`}>Create a list using customizable filters.</div>
            </button>
          }
          <button
            data-testid={`${QA_ID_PREFIX}-csv-upload-option`}
            className='recipient-option'
            onClick={handleCsvUploadButtonClick}
            disabled={loading}
          >
            <div data-testid={`${QA_ID_PREFIX}-csv-upload-icon`} className='icon-container'><CloudUpload size='large' /></div>
            <h4 data-testid={`${QA_ID_PREFIX}-csv-upload-title`}>CSV Upload</h4>
            <div data-testid={`${QA_ID_PREFIX}-csv-upload-description`}>Upload one or more CSV files.</div>
          </button>
        </div>
        {(errors.csvFiles && errors.csvFiles.message) &&
          <InputErrorText data-testid={`${QA_ID_PREFIX}-csv-files-error`} message={errors.csvFiles.message} />
        }
        {showManualRecipientInputField && 
        <>
          <Controller
            name={"cc"}
            control={control}
            render={({ field }) => (
              <Popover
                disabled={disbaleManualInput()}
                content={
                  <div
                    data-testid={`${QA_ID_PREFIX}-cc-popover-content`}
                    style={{maxWidth: "300px"}}
                  >
                    <p data-testid={`${QA_ID_PREFIX}-cc-popover-message`}>{popoverMessage}</p>
                  </div>
                }
              >
                <div data-testid={`${QA_ID_PREFIX}-cc-field-container`} style={{marginTop: "24px"}}>
                  <EmailInputField
                    {...field}
                    componentKey={`add-to-field`}
                    error={errors.cc ? errors.cc.message : null}
                    disabled={loading || disbaleManualInput()}
                    field={{
                      label: "Cc",
                      name: "cc",
                      required: true,
                    }}
                    value={manualCcRecipientFieldValue || []}
                    handleChange={(value) => {
                      if(!Array.isArray(value)) return;
                      console.log(`EmailInputField.cc.handleChange`, value);
                      console.log({value});
                      notificationRecipientsFields[RECIPIENTS.CC].handleChange(
                        value
                      );
                    }}
                    getTypedTextValue={setTypedTextValue}
                    setValueFromDropdownSelected={setValueFromDropdownSelected}
                    isLookUp={true}
                    associateOnly={true}
                    includeGroups={true}
                    multiple={true}
                    data-testid={`${QA_ID_PREFIX}-cc-email-input`}
                  />
                </div>
              </Popover>
            )}
          />
          <Controller
            name={"bcc"}
            control={control}
            render={({ field }) => (
              <Popover
                disabled={disbaleManualInput()}
                content={
                  <div
                    data-testid={`${QA_ID_PREFIX}-bcc-popover-content`}
                    style={{maxWidth: "300px"}}
                  >
                    <p data-testid={`${QA_ID_PREFIX}-bcc-popover-message`}>{popoverMessage}</p>
                  </div>
                }
              >
                <div data-testid={`${QA_ID_PREFIX}-bcc-field-container`} style={{marginTop: "24px"}}>
                  <EmailInputField
                    {...field}
                    componentKey={`add-to-field`}
                    error={errors.bcc ? errors.bcc.message : null}
                    disabled={loading || disbaleManualInput()}
                    field={{
                      label: "Bcc",
                      name: "bcc",
                      required: true,
                    }}
                    value={manualBccRecipientFieldValue || []}
                    handleChange={(value) => {
                      if(!Array.isArray(value)) return;
                      console.log(`EmailInputField.bcc.handleChange`, value);
                      console.log({value});
                      notificationRecipientsFields[RECIPIENTS.BCC].handleChange(
                        value
                      );
                    }}
                    getTypedTextValue={setTypedTextValue}
                    setValueFromDropdownSelected={setValueFromDropdownSelected}
                    isLookUp={true}
                    associateOnly={true}
                    includeGroups={true}
                    multiple={true}
                    data-testid={`${QA_ID_PREFIX}-bcc-email-input`}
                  />
                </div>
              </Popover>
            )}
          />
        </>
        }
        <EditFormActionsFooter
          data-testid={`${QA_ID_PREFIX}-actions-footer`}
          stepIndex={stepIndex}
          hidePreviousButton={stepIndex === 0}
          loading={continueButtonLoading}
          onPrevious={() => handlePrevious()}
          onSaveDraft={() => saveDraft()}
          onContinue={() => handleContinue(stepIndex)}
        />
      </div>
    </div>
      <CSVUploadOverlay
        data-testid={`${QA_ID_PREFIX}-csv-upload-overlay`}
        open={showCSVUploadOverlay}
        handleClose={() => setShowCSVUploadOverlay(false)}
        formValues={formValues}
      />
      <SavedRecipientsOverlay
        data-testid={`${QA_ID_PREFIX}-saved-recipients-overlay`}
        open={showSavedRecipientsOverlay}
        handleClose={() => setShowSavedRecipientsOverlay(false)}
        onFilterApplied={(filterData)=>{
          handleSavedFilterApplied({filterName: filterData?.name, rawFilter: filterData?.filter, filterId: filterData?.filterId})
        }}
        loading={loading}
      />
      <RecipientFilterOverlay
        data-testid={`${QA_ID_PREFIX}-recipient-filter-overlay`}
        open={showRecipientFilterOverlay}
        handleClose={() => {
          setShowRecipientFilterOverlay(false);
          setFilterId(null);
          setRawFilter("");
        }}
        onApplyCallback={handleSavedFilterApplied}
        loading={loading}
      />
    </>
  )
}

export default SelectRecipientsEdit;